import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarPlus, Clock, Download, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { Donut } from '@/components/dashboard/charts';
import { LongExpiredToggle, RenewFlag, StatusBadge } from '@/components/csp/CspKit';
import { exportCsv, exportIcs, fmtDate, sortRows, useCspRows, type CspRow, type CspSort, type CspSortKey } from '@/lib/csp';
import { cspLicencesView, ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * CSP → Dashboard. Renewal view over the Partner Center NCE report: what's active, what's
 * expiring soon, what has failed auto-renewal, and what needs a manual decision.
 */
export default function CspDashboardPage() {
  const { query, dataset, visible, hidden, prefs } = useCspRows({ scoped: true });
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<CspSort>({ key: 'days', dir: 'asc' });

  const stats = useMemo(() => ({
    customers: new Set(visible.map((r) => r.customer)).size,
    seats: visible.reduce((s, r) => s + r.quantity, 0),
    active: visible.filter((r) => r.status === 'active').length,
    expiring: visible.filter((r) => r.status === 'expiring').length,
    expired: visible.filter((r) => r.status === 'expired').length,
  }), [visible]);

  // Upcoming: hide expired rows unless they're inside the 7-day post-expiry window with
  // auto-renew ON — recently failed auto-renewals worth chasing.
  const upcoming = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = visible.filter((r) =>
      (!s || r.customer.toLowerCase().includes(s) || r.plan.toLowerCase().includes(s) || r.accountManager.toLowerCase().includes(s))
      && (r.status !== 'expired' || (r.daysLeft != null && r.daysLeft >= -7 && r.autoRenew)));
    return sortRows(rows, sort).slice(0, 25);
  }, [visible, q, sort]);

  const soonest = useMemo(() => visible.filter((r) => r.daysLeft != null && r.daysLeft >= 0).sort((a, b) => a.daysLeft! - b.daysLeft!).slice(0, 6), [visible]);
  // Expired + auto-renew ON = a renewal that should have happened but didn't. Newest first.
  const failed = useMemo(() => visible.filter((r) => r.status === 'expired' && r.autoRenew).sort((a, b) => b.daysLeft! - a.daysLeft!), [visible]);

  const staleDays = dataset ? Math.floor((Date.now() - new Date(dataset.uploadedAt).getTime()) / 86_400_000) : 0;
  const licences = (p: Record<string, string>) => navigate(cspLicencesView(p));

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
      <PageHeader
        title="NCE renewals"
        isFetching={query.isFetching}
        subtitle={dataset
          ? <>Partner Center report uploaded {fmtDate(dataset.uploadedAt)} by {dataset.uploadedBy}{hidden > 0 && <> · {hidden} long-expired hidden</>}</>
          : 'Microsoft New Commerce subscription renewals'}
        actions={<>
          <LongExpiredToggle />
          <Button variant="outline" size="sm" onClick={() => exportIcs(visible)} disabled={!visible.length} title="All renewals as an Outlook calendar (.ics)">
            <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Outlook
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportCsv(visible)} disabled={!visible.length}>
            <Download className="mr-1.5 h-3.5 w-3.5" /> CSV
          </Button>
          <Button size="sm" asChild><Link to={ROUTES.csp.upload}><Upload className="mr-1.5 h-3.5 w-3.5" /> Upload report</Link></Button>
        </>}
      />

      {query.isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : !dataset ? (
        <EmptyDataset />
      ) : (
        <>
          {staleDays >= 30 && (
            <div className={cn('mt-4 flex items-center gap-3 rounded-lg border px-4 py-3 text-sm',
              staleDays >= 60 ? 'border-destructive/30 bg-destructive/10 text-destructive' : 'border-warning/30 bg-warning-soft text-warning')}>
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <div className="flex-1">
                <strong>{staleDays >= 60 ? 'Data is critically outdated.' : 'Data may be outdated.'}</strong>{' '}
                Last uploaded {staleDays} days ago by {dataset.uploadedBy}. Export a new Microsoft Tenant Report from Partner Center and upload it.
              </div>
              <Button size="sm" variant={staleDays >= 60 ? 'destructive' : 'default'} asChild><Link to={ROUTES.csp.upload}>Upload</Link></Button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Customers" value={stats.customers} sub={`${visible.length} subscriptions · ${stats.seats} seats`} onClick={() => navigate(ROUTES.csp.customers)} />
            <StatCard label="Active" value={stats.active} sub={`more than ${prefs.threshold} days left`} tone="success" onClick={() => licences({ status: 'active' })} />
            <StatCard label="Expiring soon" value={stats.expiring} sub={`within ${prefs.threshold} days`} tone={stats.expiring ? 'warning' : 'default'} onClick={() => licences({ status: 'expiring' })} />
            <StatCard label="Expired" value={stats.expired} sub={stats.expired ? `${stats.expired} need attention` : 'All clear'} tone={stats.expired ? 'destructive' : 'success'} onClick={() => licences({ status: 'expired' })} />
          </div>

          {/* The 7-column renewals table needs ~1000px: beside the 380px rail only on very wide screens; below that the rail's cards sit in a row above it. */}
          <div className="mt-4 grid grid-cols-1 gap-4 min-[1700px]:grid-cols-[minmax(0,1fr)_380px]">
            <section className="min-w-0 rounded-lg border border-border bg-card shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-divider px-5 py-3">
                <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Upcoming renewals</h2>
                <SearchInput withIcon placeholder="Customer, plan or account manager" className="w-72" value={q} onValueChange={setQ} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <SortTh k="customer" sort={sort} setSort={setSort}>Customer</SortTh>
                      <SortTh k="plan" sort={sort} setSort={setSort}>Plan</SortTh>
                      <SortTh k="status" sort={sort} setSort={setSort}>Status</SortTh>
                      <SortTh k="quantity" sort={sort} setSort={setSort} right>Qty</SortTh>
                      <SortTh k="commitment" sort={sort} setSort={setSort}>Term</SortTh>
                      <SortTh k="autorenew" sort={sort} setSort={setSort}>Renew</SortTh>
                      <SortTh k="days" sort={sort} setSort={setSort}>Renewal</SortTh>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {upcoming.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No matching renewals.</td></tr>}
                    {upcoming.map((r) => (
                      <tr key={r.key} className="cursor-pointer hover:bg-muted/30" onClick={() => licences({ client: r.customer })}>
                        <td className="max-w-[220px] truncate px-3 py-2 font-medium text-foreground" title={r.customer}>{r.customer}</td>
                        <td className="max-w-[280px] truncate px-3 py-2" title={r.plan}>{r.plan}</td>
                        <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.quantity || '—'}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{r.commitment || '—'}</td>
                        <td className="px-3 py-2"><RenewFlag on={r.autoRenew} /></td>
                        <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                          {fmtDate(r.endDate)}{r.daysLeft != null && <span className="ml-1 text-xs text-muted-foreground">({r.daysLeft}d)</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="order-first grid grid-cols-1 gap-4 lg:grid-cols-3 min-[1700px]:order-none min-[1700px]:flex min-[1700px]:flex-col">
              <ChartCard title="Status breakdown">
                <Donut height={180} valueFormatter={(n) => String(n)}
                  data={[
                    { id: 'active', label: 'Active', value: stats.active, color: 'hsl(var(--success))' },
                    { id: 'expiring', label: 'Expiring soon', value: stats.expiring, color: 'hsl(var(--warning))' },
                    { id: 'expired', label: 'Expired', value: stats.expired, color: 'hsl(var(--destructive))' },
                  ].filter((b) => b.value > 0)}
                  onSelect={(b) => licences({ status: String(b.id) })} />
              </ChartCard>

              <ChartCard title="Soonest renewals">
                {soonest.length === 0 ? <Empty>No upcoming renewals</Empty> : (
                  <ul className="space-y-2.5">
                    {soonest.map((r) => <TimelineItem key={r.key} r={r} icon="clock"
                      sub={<>{fmtDate(r.endDate)} · {r.daysLeft} days · {r.autoRenew ? 'auto-renew on' : <span className="text-destructive">auto-renew off</span>}</>} />)}
                  </ul>
                )}
              </ChartCard>

              <ChartCard title="Expired with auto-renew on" action={failed.length > 0 && <span className="text-xs text-muted-foreground">{failed.length}</span>}>
                {failed.length === 0 ? <Empty>No failed auto-renewals</Empty> : (
                  <ul className="max-h-72 space-y-2.5 overflow-y-auto">
                    {failed.map((r) => <TimelineItem key={r.key} r={r} icon="alert"
                      sub={<>Expired {fmtDate(r.endDate)} · {Math.abs(r.daysLeft!)}d ago</>} />)}
                  </ul>
                )}
              </ChartCard>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function EmptyDataset() {
  return (
    <div className="mx-auto mt-16 max-w-md text-center">
      <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-3 text-base font-semibold text-foreground">No subscription data yet</h2>
      <p className="mt-1 text-sm text-muted-foreground">Upload the Partner Center "Microsoft Tenant Report" CSV to start tracking NCE renewals.</p>
      <Button className="mt-4" size="sm" asChild><Link to={ROUTES.csp.upload}>Upload report</Link></Button>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="py-6 text-center text-xs text-muted-foreground">{children}</div>;
}

function TimelineItem({ r, sub, icon }: { r: CspRow; sub: React.ReactNode; icon: 'clock' | 'alert' }) {
  const Icon = icon === 'clock' ? Clock : AlertTriangle;
  return (
    <li className="flex items-start gap-3">
      <span className={cn('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
        icon === 'alert' ? 'bg-destructive/10 text-destructive' : r.status === 'expiring' ? 'bg-warning-soft text-warning' : 'bg-muted text-muted-foreground')}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-foreground" title={`${r.customer} — ${r.plan}`}><span className="font-medium">{r.customer}</span> · {r.plan}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
      <StatusBadge status={r.status} label={`${r.daysLeft}d`} />
    </li>
  );
}

export function SortTh({ k, sort, setSort, right, children }: {
  k: CspSortKey; sort: CspSort; setSort: (s: CspSort) => void; right?: boolean; children: React.ReactNode;
}) {
  const active = sort.key === k;
  return (
    <th className={cn('cursor-pointer select-none whitespace-nowrap px-3 py-2 font-medium hover:text-foreground', right && 'text-right')}
      onClick={() => setSort({ key: k, dir: active && sort.dir === 'asc' ? 'desc' : 'asc' })}>
      {children}<span className={cn('ml-1', !active && 'opacity-30')}>{active && sort.dir === 'desc' ? '↓' : '↑'}</span>
    </th>
  );
}
